import { useEffect } from 'react';
import { useI18n } from '../i18n';

interface SEOProps {
    title?: string;
    description?: string;
    keywords?: string[];
    ogImage?: string;
    ogType?: 'website' | 'article';
    twitterCard?: 'summary' | 'summary_large_image';
}

export function useSEO({
    title,
    description,
    keywords = [],
    ogImage = '/og-image.png',
    ogType = 'website',
    twitterCard = 'summary_large_image',
}: SEOProps = {}) {
    const { locale } = useI18n();

    useEffect(() => {
        // Update document title
        if (title) {
            document.title = `${title} - GetDev.Tools`;
        }

        // Update lang attribute
        document.documentElement.lang = locale;

        // Update or create meta tags
        const updateMetaTag = (name: string, content: string, property = false) => {
            const attr = property ? 'property' : 'name';
            let element = document.querySelector(`meta[${attr}="${name}"]`);
            
            if (!element) {
                element = document.createElement('meta');
                element.setAttribute(attr, name);
                document.head.appendChild(element);
            }
            
            element.setAttribute('content', content);
        };

        // Basic meta tags
        if (description) {
            updateMetaTag('description', description);
        }

        if (keywords.length > 0) {
            updateMetaTag('keywords', keywords.join(', '));
        }

        // Open Graph tags
        const ogUrl = window.location.href;
        const fullTitle = title ? `${title} - GetDev.Tools` : 'GetDev.Tools - Developer Tools Collection';
        
        updateMetaTag('og:title', fullTitle, true);
        updateMetaTag('og:type', ogType, true);
        updateMetaTag('og:url', ogUrl, true);
        
        if (description) {
            updateMetaTag('og:description', description, true);
        }
        
        updateMetaTag('og:image', `${window.location.origin}${ogImage}`, true);
        updateMetaTag('og:site_name', 'GetDev.Tools', true);
        updateMetaTag('og:locale', locale.replace('-', '_'), true);

        // Twitter Card tags
        updateMetaTag('twitter:card', twitterCard);
        updateMetaTag('twitter:title', fullTitle);
        
        if (description) {
            updateMetaTag('twitter:description', description);
        }
        
        updateMetaTag('twitter:image', `${window.location.origin}${ogImage}`);

        // Canonical URL
        let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
        if (!canonical) {
            canonical = document.createElement('link');
            canonical.setAttribute('rel', 'canonical');
            document.head.appendChild(canonical);
        }
        canonical.href = ogUrl;

    }, [title, description, keywords, ogImage, ogType, twitterCard, locale]);
}
